import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogActions,
  Stack,
  Tooltip,
  TextField,
  Typography,
  InputAdornment,
  IconButton,
  Chip,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';

// ─── Base Rate helpers ───────────────────────────────────────────────────────

const emptyBaseForm = { startKM: '', endKM: '', baseRate: '' };

/**
 * Returns true if [aStart, aEnd] and [bStart, bEnd] overlap (closed intervals).
 */
const rangesOverlap = (aStart, aEnd, bStart, bEnd) => aStart <= bEnd && aEnd >= bStart;

const validateBaseForm = (form, existing, editId = null) => {
  const errors = {};
  const from = Number(form.startKM);
  const to = Number(form.endKM);

  if (form.startKM === '' || isNaN(from) || from < 0) errors.startKM = 'Enter a valid start distance (≥ 0)';
  if (form.endKM === '' || isNaN(to) || to < 0) errors.endKM = 'Enter a valid end distance (≥ 0)';
  if (!errors.startKM && !errors.endKM && to <= from) errors.endKM = 'Must be greater than From distance';
  if (!form.baseRate || isNaN(Number(form.baseRate)) || Number(form.baseRate) < 0)
    errors.baseRate = 'Enter a valid base rate';

  if (!errors.startKM && !errors.endKM) {
    const conflict = existing.find(r => r.id !== editId && rangesOverlap(from, to, r.startKM, r.endKM));
    if (conflict) {
      const msg = `Overlaps existing range ${conflict.startKM}–${conflict.endKM} km`;
      errors.startKM = msg;
      errors.endKM = msg;
    }
  }

  return errors;
};

// ─── Base Rate Add / Edit Modal ───────────────────────────────────────────────

const BaseRateFormModal = ({ open, onClose, onSave, editData, existingRates }) => {
  const isEdit = Boolean(editData);
  const [form, setForm] = useState(emptyBaseForm);
  const [errors, setErrors] = useState({});

  React.useEffect(() => {
    if (open) {
      setForm(
        isEdit
          ? { startKM: String(editData.startKM), endKM: String(editData.endKM), baseRate: String(editData.baseRate) }
          : emptyBaseForm,
      );
      setErrors({});
    }
  }, [open, editData]);

  const handleChange = e => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
    setErrors(prev => ({ ...prev, startKM: undefined, endKM: undefined, [name]: undefined }));
  };

  const handleSubmit = () => {
    const validationErrors = validateBaseForm(form, existingRates, editData?.id);

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    onSave({
      id: editData?.id,
      startKM: Number(form.startKM),
      endKM: Number(form.endKM),
      baseRate: Number(form.baseRate),
    });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        {isEdit ? 'Edit Base Rate' : 'Add Base Rate'}
        <IconButton size="small" onClick={onClose}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <Box px={3} pt={1} pb={2}>
        <Typography
          variant="caption"
          color="text.secondary"
          fontWeight={600}
          textTransform="uppercase"
          display="block"
          mb={1}>
          Distance Range
        </Typography>
        <Stack direction="row" spacing={2}>
          <TextField
            fullWidth
            size="small"
            type="number"
            name="startKM"
            label="From (km)"
            value={form.startKM}
            onChange={handleChange}
            error={Boolean(errors.startKM)}
            helperText={errors.startKM}
            InputProps={{ endAdornment: <InputAdornment position="end">km</InputAdornment> }}
          />
          <TextField
            fullWidth
            size="small"
            type="number"
            name="endKM"
            label="To (km)"
            value={form.endKM}
            onChange={handleChange}
            error={Boolean(errors.endKM)}
            helperText={errors.endKM}
            InputProps={{ endAdornment: <InputAdornment position="end">km</InputAdornment> }}
          />
        </Stack>

        <Typography
          variant="caption"
          color="text.secondary"
          fontWeight={600}
          textTransform="uppercase"
          display="block"
          mt={2}
          mb={1}>
          Base Rate
        </Typography>
        <TextField
          fullWidth
          size="small"
          type="number"
          name="baseRate"
          label="Base Rate"
          value={form.baseRate}
          onChange={handleChange}
          error={Boolean(errors.baseRate)}
          helperText={errors.baseRate}
          InputProps={{ startAdornment: <InputAdornment position="start">€</InputAdornment> }}
        />
      </Box>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button variant="outlined" onClick={onClose}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleSubmit} disabled={!form.startKM || !form.endKM || !form.baseRate}>
          {isEdit ? 'Update' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ─── Base Rate Delete Confirm Modal ──────────────────────────────────────────

const BaseRateDeleteModal = ({ open, onClose, onConfirm, row }) => (
  <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
    <DialogTitle>Delete Base Rate</DialogTitle>
    <Box px={3} py={2}>
      <Typography>
        Are you sure you want to delete the base rate for{' '}
        <strong>
          {row?.startKM} – {row?.endKM} km
        </strong>
        ? This action cannot be undone.
      </Typography>
    </Box>
    <DialogActions sx={{ px: 3, pb: 2 }}>
      <Button variant="outlined" onClick={onClose}>
        Cancel
      </Button>
      <Button variant="contained" color="error" onClick={onConfirm}>
        Delete
      </Button>
    </DialogActions>
  </Dialog>
);

// ─── Base Rate Manager Dialog ─────────────────────────────────────────────────

const BaseRateManagerDialog = ({
  open,
  onClose,
  baseRates,
  loading,
  fetchBaseRates,
  addBaseRate,
  updateBaseRate,
  deleteBaseRate,
}) => {
  const [showForm, setShowForm] = useState(false);
  const [editRow, setEditRow] = useState(null);
  const [deleteRow, setDeleteRow] = useState(null);

  useEffect(() => {
    if (open) fetchBaseRates();
  }, [open, fetchBaseRates]);

  const nextId = useCallback(() => Math.max(0, ...baseRates.map(r => r.id)) + 1, [baseRates]);

  const openAdd = () => {
    setEditRow(null);
    setShowForm(true);
  };
  const openEdit = row => {
    setEditRow(row);
    setShowForm(true);
  };

  const handleSave = async data => {
    console.log('Data ===', data);

    try {
      if (data.id) {
        await updateBaseRate(data);
      } else {
        await addBaseRate(data);
      }
      setShowForm(false);
      setEditRow(null);
    } catch (err) {
      console.error('Save failed', err);
    }
  };

  const handleDelete = async () => {
    if (deleteRow) {
      await deleteBaseRate(deleteRow.id);
      setDeleteRow(null);
    }
  };

  const sorted = [...baseRates].sort((a, b) => a.startKM - b.startKM);

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Manage Base Rates
          <Box display={'flex'} gap={2}>
            <Button size="small" variant="contained" startIcon={<AddIcon />} onClick={openAdd}>
              Add Base Rate
            </Button>
            <IconButton size="small" onClick={onClose}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        </DialogTitle>

        <Box px={3} py={2}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
            <thead>
              <tr style={{ borderBottom: '1px solid rgba(0,0,0,0.12)' }}>
                <th style={{ textAlign: 'left', padding: '8px 10px', fontWeight: 600, color: '#666' }}>Distance Range</th>
                <th style={{ textAlign: 'center', padding: '8px 10px', fontWeight: 600, color: '#666' }}>Base Rate</th>
                <th style={{ textAlign: 'center', padding: '8px 10px', fontWeight: 600, color: '#666' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {sorted.length === 0 && (
                <tr>
                  <td colSpan={3} style={{ textAlign: 'center', padding: '24px', color: '#999' }}>
                    No base rates added yet.
                  </td>
                </tr>
              )}
              {sorted.map(row => (
                <tr key={row.id} style={{ borderBottom: '1px solid rgba(0,0,0,0.06)' }}>
                  <td style={{ padding: '9px 10px' }}>
                    <Chip label={`${row.startKM} – ${row.endKM} km`} size="small" variant="outlined" />
                  </td>
                  <td style={{ padding: '9px 10px', textAlign: 'center', fontWeight: 500 }}>€{row.baseRate.toFixed(2)}</td>
                  <td style={{ padding: '9px 10px', textAlign: 'center' }}>
                    <Stack direction="row" spacing={1} justifyContent="center">
                      <Tooltip title="Edit">
                        <Button size="small" variant="outlined" color="primary" onClick={() => openEdit(row)}>
                          <EditIcon sx={{ fontSize: 15, mr: 0.5 }} /> Edit
                        </Button>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <Button size="small" variant="outlined" color="error" onClick={() => setDeleteRow(row)}>
                          <DeleteIcon sx={{ fontSize: 15, mr: 0.5 }} /> Delete
                        </Button>
                      </Tooltip>
                    </Stack>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Box>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button variant="outlined" onClick={onClose}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add / Edit sub-modal */}
      <BaseRateFormModal
        open={showForm}
        onClose={() => {
          setShowForm(false);
          setEditRow(null);
        }}
        onSave={handleSave}
        editData={editRow}
        existingRates={baseRates}
      />

      {/* Delete confirm sub-modal */}
      <BaseRateDeleteModal
        open={Boolean(deleteRow)}
        onClose={() => setDeleteRow(null)}
        onConfirm={handleDelete}
        row={deleteRow}
      />
    </>
  );
};

export default BaseRateManagerDialog;
